import * as changeCase from "change-case";
import { X } from "lucide-react";
import { useEffect } from "react";
import React from "react";
import { useFormContext } from "react-hook-form";
import { cn, ui } from "@patient-watch/ui";
import { RequiredMarker } from "./RequiredMarker";
import { getOptionTestId } from "./utils/getTestId";
import { reactNodeToPlainString } from "./utils/reactNodeToPlainString";
import type { LooseRHFField } from "./utils/loose-rhf-field";

export const arrayToOptions = (arr: string[]): OptionT[] => {
    return arr.map((item) => ({
        value: item,
        label: item,
    }));
};

export interface OptionT {
    value: string;
    label: React.ReactNode;
    description?: string;
    searchText?: string;
    locked?: boolean;
    icon?: React.ReactNode;
}

interface Props {
    name: string;
    description?: string;
    placeholder?: string;
    required?: boolean;
    label?: React.ReactNode;
    disabled?: boolean;
    options: OptionT[];
    side?: "top" | "bottom";
    size?: "sm" | "md" | "lg";
    autoFocus?: boolean;
    clearable?: boolean;
    dependsOn?: string; // Add this prop to specify which field this select depends on
    addOptionButton?: React.ReactNode;
}

const Select = ({
    name,
    description,
    label,
    options,
    placeholder,
    side,
    size,
    autoFocus,
    dependsOn,
    clearable,
    required,
    disabled,
    addOptionButton,
}: Props) => {
    const form = useFormContext();
    const { control, setValue } = form;

    useEffect(() => {
        if (dependsOn && options?.length === 1) {
            // If there's only one option and this field depends on another, set it as default
            setValue(name, options[0]?.value);
        }
    }, [dependsOn, options, setValue, name]);

    const orderedOptions = React.useMemo(() => {
        if (!options) return [];
        const unique = [...new Map(options.map((o) => [o.value, o])).values()];
        return [...unique].toSorted((a: OptionT, b: OptionT) =>
            reactNodeToPlainString(a.label).localeCompare(reactNodeToPlainString(b.label))
        );
    }, [options]);

    if (!options) {
        return <p className="text-sm">No Options Provided</p>;
    }

    return (
        <ui.FormField
            control={control}
            name={name}
            render={({ field }: { field: LooseRHFField }) => (
                <ui.FormItem className="w-full">
                    {!!label && (
                        <ui.FormLabel
                            className={cn(
                                size === "sm" && "text-xs",
                                size === "md" && "text-sm",
                                size === "lg" && "text-base"
                            )}
                        >
                            {label}
                            {required && <RequiredMarker />}
                        </ui.FormLabel>
                    )}
                    {!!description && <ui.FormDescription>{description}</ui.FormDescription>}
                    <ui.Select
                        disabled={disabled}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                    >
                        <ui.FormControl>
                            <div className="flex flex-row items-center">
                                <ui.SelectTrigger
                                    testId={`${changeCase.kebabCase(name)}-trigger`}
                                    autoFocus={autoFocus}
                                    size={size === "lg" ? "md" : size === "sm" ? "sm" : "md"}
                                >
                                    <ui.SelectValue
                                        placeholder={placeholder ?? <span className="text-slate-500">Select</span>}
                                    />
                                </ui.SelectTrigger>
                                {clearable && field.value != null && field.value !== "" && (
                                    <ui.Button
                                        tabIndex={-1}
                                        variant="outline"
                                        size="icon"
                                        type="button"
                                        className="bg-white bg-opacity-100 ml-2 rounded-md w-12 h-12 text-slate-600"
                                        onClick={() => {
                                            field.onChange(null);
                                        }}
                                    >
                                        <X size={16} />
                                        <span className="sr-only">Clear</span>
                                    </ui.Button>
                                )}
                            </div>
                        </ui.FormControl>
                        <ui.SelectContent side={side ?? "bottom"}>
                            {orderedOptions.length > 8 ? (
                                <ui.ScrollArea className="h-[300px]">
                                    {orderedOptions.map((option) => (
                                        <ui.SelectItem
                                            data-testid={getOptionTestId(option)}
                                            className="flex justify-between-between items-center"
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.locked}
                                        >
                                            <p>{option.label}</p>
                                        </ui.SelectItem>
                                    ))}
                                    <ui.ScrollBar orientation="vertical" />
                                </ui.ScrollArea>
                            ) : (
                                orderedOptions.map((option) => (
                                    <ui.SelectItem
                                        data-testid={getOptionTestId(option)}
                                        className="flex flex-row justify-between items-center w-full"
                                        key={option.value}
                                        value={option.value}
                                        disabled={option.locked}
                                    >
                                        <p className="flex flex-row justify-between items-center gap-x-2 w-full">
                                            {option.label}
                                        </p>
                                    </ui.SelectItem>
                                ))
                            )}
                        </ui.SelectContent>
                    </ui.Select>
                    <ui.FormMessage />

                    {addOptionButton}
                </ui.FormItem>
            )}
        />
    );
};

export default Select;
